import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import Select from "react-select";
import {
  clearBusinessTax,
  clearBusinessTaxError,
  requestCreateBusinessTax,
  requestListBusiness,
  useAuthState,
  useBusinessTaxDispatch,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useBusinessTaxState,
  requestGetBusinessTax,
  requestUpdateBusinessTax,
  requestListAccount,
  useAccountingState,
  useAccountingDispatch,
  clearInitAccountStatus,
  requestInitAccount,
  requestListDebitAccount,
} from "../../Context";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { cancelAPIRequest, classNames, getErrorMessages } from "../../Utils";
import { NumericFormat } from "react-number-format";
import FormLabel from "../../Components/FormLabel";
import EmptyState from "../../Components/EmptyState";
import ErrorSelect from "../../Components/ErrorSelect";
import {
  purchaseAccountCategories,
  resolveDefaultTaxAccount,
  salesAccountCategories,
  TAX_PERCENTAGE_MAX,
  TAX_PERCENTAGE_MIN,
  taxComputationOptions,
  taxTypesOptions,
} from "../../Constants";

export const TaxCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedBusiness, setselectedBusiness] = useState(null);
  const [taxName, settaxName] = useState("");
  const [type, settype] = useState("SALES");
  const [computation, setcomputation] = useState("PERCENTAGE");
  const [amount, setamount] = useState({ value: null, floatValue: null });
  const [debitAccount, setdebitAccount] = useState(null);
  const [fieldError, setfieldError] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [typeError, settypeError] = useState(null);
  const [computationError, setcomputationError] = useState(null);
  const [amountError, setamountError] = useState(null);
  const [debitAccountError, setdebitAccountError] = useState(null);
  const [nameError, setnameError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const { id: taxIdParam } = useParams();

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const businessTaxDispatch = useBusinessTaxDispatch();
  const accountingDispatch = useAccountingDispatch();

  const { businesses, businessLoading } = useBusinessTransactionState();

  const {
    newTax,
    newTaxLoading,
    newTaxError,
    businessTax,
    businessTaxLoading,
    businessTaxError,
    taxID,
    updateTaxLoading,
    updateTaxError,
  } = useBusinessTaxState();

  const {
    debitAccountRequest,
    accounts,
    accountLoading,
    accountError,
    initAccountLoading,
    initAccountStatus,
    debitLoading,
    debits,
    debitError,
  } = useAccountingState();

  const { user } = useAuthState();

  const isFormDisabled = businessTax?.id;

  useEffect(() => {
    clearBusinessTax(businessTaxDispatch);
    clearBusinessTaxError(businessTaxDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (debitAccountRequest) {
      cancelAPIRequest(debitAccountRequest);
    }
    onLoadDebitAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, type]);

  useEffect(() => {
    if (debits.length > 0) {
      let selectedDebit;
      if (businessTax?.id) {
        selectedDebit = debits.find(
          (debit) => debit.id === businessTax.accountID
        );
      } else {
        // This page has no `kind` field, so every tax it creates is treated as
        // a PPN — which is all it could make before kinds existed.
        selectedDebit = resolveDefaultTaxAccount(debits, null, type);
      }

      if (selectedDebit) {
        setdebitAccount({
          ...selectedDebit,
          label: selectedDebit.name,
          value: selectedDebit.id,
        });
      } else {
        setdebitAccount(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debits, businessTax]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(accountingDispatch, {
        businessID: selectedBusiness.id,
      });
      clearInitAccountStatus(accountingDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  useEffect(() => {
    if (businesses.length > 0) {
      if (businessIdParam) {
        const business = businesses.find(
          (item) => item.value === businessIdParam
        );
        if (business) {
          setselectedBusiness({
            ...business,
            label: business.name,
            value: business.id,
          });
        }
      } else if (taxIdParam) {
        const business = businesses.find(
          (item) => item.value === businessTax?.businessID
        );
        if (business) {
          setselectedBusiness({
            ...business,
            label: business.name,
            value: business.id,
          });
        }
      } else {
        setselectedBusiness(businesses[0]);
      }
    }
  }, [businessIdParam, taxIdParam, businesses, businessTax]);

  useEffect(() => {
    if (taxIdParam) {
      requestGetBusinessTax(businessTaxDispatch, {
        id: taxIdParam,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxIdParam]);

  useEffect(() => {
    if (businessTax?.id && businessTax?.businessID) {
      const { name, type, computation, amount } = businessTax;

      settaxName(name);
      settype(type);
      setcomputation(computation);
      setamount({ value: amount, floatValue: amount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTax?.id]);

  useEffect(() => {
    if (newTax || taxID) {
      setshowSuccessModal(true);
    }
  }, [newTax, taxID]);

  useEffect(() => {
    if (submitted) {
      const isNameValid = taxName.length >= 1;
      const isDebitValid = Boolean(debitAccount?.value);
      if (isNameValid) {
        setErrorMessage("name", "");
      } else {
        setErrorMessage("name", "Tax name is required.");
      }

      if (isDebitValid) {
        setErrorMessage("debitAccount", "");
      } else {
        setErrorMessage("debitAccount", "Account is required.");
      }
    }
  }, [taxName, submitted, debitAccount]);

  useEffect(() => {
    if (submitted && amount) {
      if (!amount.value) {
        setErrorMessage("amount", "taxes.errors.amountRequired");
      } else if (!isAmountInRange(amount.floatValue, computation)) {
        setErrorMessage("amount", "taxes.errors.percentageRange");
      } else {
        setErrorMessage("amount", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, computation, submitted]);

  useEffect(() => {
    if (submitted && computation) {
      const isComputationValid = taxComputationOptions.some(
        (taxComputation) => taxComputation.value === computation
      );
      if (isComputationValid) {
        setErrorMessage("computation", "");
      } else {
        setErrorMessage("computation", "Please select a valid computation.");
      }
    }
  }, [computation, submitted]);

  useEffect(() => {
    if (submitted && type) {
      const isTypeValid = taxTypesOptions.some(
        (taxType) => taxType.value === type
      );
      if (isTypeValid) {
        setErrorMessage("type", "");
      } else {
        setErrorMessage("type", "Please select a valid type.");
      }
    }
  }, [type, submitted]);

  const onLoadDebitAccount = () => {
    if (selectedBusiness?.id || businessIdParam) {
      let accountCategories;

      if (type === "SALES") {
        accountCategories = salesAccountCategories;
      }

      if (type === "PURCHASES") {
        accountCategories = purchaseAccountCategories;
      }

      requestListDebitAccount(accountingDispatch, {
        businessID: selectedBusiness?.id || businessIdParam,
        categories: accountCategories,
      });
    }
  };

  // Same bounds as createBusinessTax/updateBusinessTax and the v2 TaxModal.
  // Without this a 500% rate only fails at AppSync, as an opaque ValidationError.
  const isAmountInRange = (value, taxComputation) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return false;
    if (taxComputation !== "PERCENTAGE") return true;
    return parsed >= TAX_PERCENTAGE_MIN && parsed <= TAX_PERCENTAGE_MAX;
  };

  const validateInput = () => {
    let currentValidName = true;
    let currentValidAmount = true;
    let currentValidType = true;
    let currentValidComputation = true;
    let currentValidDebitAccount = true;

    const isAmountValid =
      Boolean(amount.value) && isAmountInRange(amount.floatValue, computation);
    const isNameValid = taxName.length >= 1;
    const isTypeValid = taxTypesOptions.some(
      (taxType) => taxType.value === type
    );
    const isComputationValid = taxComputationOptions.some(
      (taxComputation) => taxComputation.value === computation
    );
    const isDebitValid = Boolean(debitAccount?.value);

    currentValidName = isNameValid;
    currentValidAmount = isAmountValid;
    currentValidType = isTypeValid;
    currentValidComputation = isComputationValid;
    currentValidDebitAccount = isDebitValid;

    return (
      currentValidName &&
      currentValidAmount &&
      currentValidType &&
      currentValidComputation &&
      currentValidDebitAccount
    );
  };

  const onSave = (update = false) => {
    setsubmitted(true);
    const validForm = validateInput();

    if (
      (businessIdParam || selectedBusiness.value) &&
      taxName &&
      computation &&
      type &&
      amount.value &&
      debitAccount?.value
    ) {
      setfieldError(false);
    } else {
      setfieldError(true);
      return;
    }

    if (validForm) {
      const input = {
        businessID: businessIdParam || selectedBusiness.value,
        name: taxName,
        computation,
        type,
        amount: amount.value,
        accountID: debitAccount.value,
      };

      if (update) {
        input.id = businessTax.id;
        requestUpdateBusinessTax(businessTaxDispatch, { input });
      } else {
        requestCreateBusinessTax(businessTaxDispatch, { input });
      }
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "type":
        settypeError(message);
        break;
      case "computation":
        setcomputationError(message);
        break;
      case "amount":
        setamountError(message);
        break;
      case "name":
        setnameError(message);
        break;
      case "debitAccount":
        setdebitAccountError(message);
        break;
      default:
        break;
    }
  };

  const navigateToTax = () => {
    setshowSuccessModal(false);
    if (!newTaxError) {
      if (newTax) {
        navigate(`/tax?id=${businessIdParam}`);
        clearBusinessTax(businessTaxDispatch);
      }
    }
    if (!updateTaxError) {
      if (taxID) {
        navigate(`/tax?id=${selectedBusiness.value}`);
        clearBusinessTax(businessTaxDispatch);
      }
    }
  };

  const handleInitAccount = () => {
    requestInitAccount(accountingDispatch, {
      businessID: selectedBusiness?.id,
    });
  };

  const sortAccountCode = (a, b) => {
    const aCode = parseInt(a.code, 10);
    const bCode = parseInt(b.code, 10);
    return (aCode < 100000) - (bCode < 100000) || aCode - bCode;
  };

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <InputSkeleton rows={2} />
    ) : (
      <EmptyState
        className="max-w-full"
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col lg:flex-row sm:flex-col justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() =>
              navigate(`/account/import?id=${selectedBusiness.id}`)
            }
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  const renderDebitAccount = () => {
    return (
      <>
        {debitError ? (
          <ErrorSelect
            name="account"
            label="Account"
            error={debitError}
            onRefresh={onLoadDebitAccount}
          />
        ) : (
          <div>
            <FormLabel htmlFor="account" label={t("Account")} required />
            <Select
              name="account"
              className={classNames(
                debitError || debitAccountError
                  ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 text-sm"
              )}
              classNamePrefix="select2-selection"
              value={debitAccount}
              onChange={setdebitAccount}
              options={debits
                .slice()
                .sort(sortAccountCode)
                .map((obj) => ({
                  value: obj.id,
                  label: `${obj.code} - ${obj.name}`,
                }))}
              placeholder={t("Choose Account")}
              isDisabled={!type || debitLoading || isFormDisabled}
              isLoading={debitLoading}
              menuPortalTarget={document.body}
            />
            {debitAccountError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(debitAccountError)}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow sm:overflow-hidden rounded-md sm:rounded-lg">
      <form onSubmit={onSave}>
        <div className="space-y-6 px-4 py-6 sm:p-6">
          {(newTax || taxID) && (
            <ModalConfirm
              open={showSuccessModal}
              setOpen={(open) => {
                setshowSuccessModal(open);
              }}
              title="Success"
              description={
                taxIdParam
                  ? "The tax has been successfully changed"
                  : "Your new tax has been created."
              }
              type="SUCCESS"
              onConfirm={navigateToTax}
            />
          )}
          {fieldError && (
            <Alert
              title="Incomplete Form"
              type="warning"
              message="Please fill out all fields."
            />
          )}
          {(newTaxError || updateTaxError) && (
            <Alert
              title="Error"
              type="error"
              messages={getErrorMessages(newTaxError || updateTaxError, t)}
            />
          )}
          {businessTaxError && (
            <Alert
              title="Error"
              type="error"
              messages={getErrorMessages(businessTaxError, t)}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {newTaxLoading || businessTaxLoading || updateTaxLoading ? (
              <InputSkeleton rows={3} />
            ) : (
              <>
                <div>
                  <FormLabel
                    htmlFor="business"
                    label={t("Business Name")}
                    required
                  />
                  <Select
                    name="business"
                    className="mt-1 text-sm"
                    classNamePrefix="select2-selection"
                    options={businesses}
                    isLoading={businessLoading}
                    isDisabled={businessLoading || isFormDisabled}
                    placeholder={t("Select Business Name")}
                    value={selectedBusiness || ""}
                    onChange={setselectedBusiness}
                  />
                </div>
                <div>
                  <FormLabel
                    htmlFor="computation"
                    label={t("Computation")}
                    required
                  />
                  <select
                    id="computation"
                    name="computation"
                    value={computation}
                    onChange={(e) => setcomputation(e.target.value)}
                    className={classNames(
                      computationError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-600"
                    )}
                    disabled={isFormDisabled}
                  >
                    {taxComputationOptions.map((taxComputation) => (
                      <option
                        key={taxComputation.value}
                        value={taxComputation.value}
                      >
                        {t(taxComputation.label)}
                      </option>
                    ))}
                  </select>
                  {computationError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(computationError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="name" label={t("Name")} required />
                  <input
                    type="text"
                    name="name"
                    value={taxName}
                    placeholder={t("Enter Tax Name")}
                    maxLength={120}
                    onChange={(e) => settaxName(e.target.value)}
                    className={classNames(
                      nameError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {nameError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(nameError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="type" label={t("Type")} required />
                  <select
                    id="type"
                    name="type"
                    value={type}
                    onChange={(e) => settype(e.target.value)}
                    className={classNames(
                      typeError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-600"
                    )}
                    disabled={isFormDisabled}
                  >
                    {taxTypesOptions.map((taxType) => (
                      <option key={taxType.value} value={taxType.value}>
                        {t(taxType.label)}
                      </option>
                    ))}
                  </select>
                  {typeError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(typeError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="amount" label={t("Amount")} required />
                  <NumericFormat
                    type="text"
                    name="amount"
                    id="amount"
                    value={amount.floatValue || ""}
                    placeholder={t("Enter Amount")}
                    thousandSeparator={computation === "FIXED" ? "." : ""}
                    decimalSeparator=","
                    displayType="input"
                    prefix={computation === "FIXED" ? "Rp " : ""}
                    suffix={computation === "FIXED" ? "" : "%"}
                    onValueChange={(values) => setamount(values)}
                    decimalScale={computation === "FIXED" ? 0 : 2}
                    className={classNames(
                      amountError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-400"
                    )}
                    disabled={isFormDisabled}
                  />
                  {amountError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(amountError)}
                    </div>
                  )}
                </div>
                <div>
                  {selectedBusiness?.id && type && accounts.length === 0 ? (
                    accountLoading ? (
                      <InputSkeleton rows={1} />
                    ) : accountError ? (
                      <>
                        <FormLabel
                          htmlFor="account"
                          label={t("Account")}
                          required
                        />
                        <Alert
                          title="Error"
                          type="error"
                          message={
                            accountError?.message ||
                            "Error fetch accounting account."
                          }
                          errors={accountError?.errors}
                        />
                      </>
                    ) : (
                      <>
                        <FormLabel
                          htmlFor="account"
                          label={t("Account")}
                          required
                        />
                        <EmptyAccount />
                      </>
                    )
                  ) : (
                    renderDebitAccount()
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
          <Button
            type="submit"
            className="w-full sm:w-auto"
            title={taxIdParam ? t("Change Tax") : t("Create Tax")}
            loading={newTaxLoading || updateTaxLoading}
            disabled={
              newTaxLoading ||
              Boolean(taxName === "") ||
              amount.value === null ||
              updateTaxLoading
            }
            onClick={(e) => {
              e.preventDefault();
              taxIdParam ? onSave(true) : onSave();
            }}
          />
        </div>
      </form>
    </div>
  );
};
